import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  customerService,
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerSearchDto,
  CreateCustomerWithJobDto,
  CreateCustomerWithJobResponse,
} from '../services/customerService';
import { queryKeys } from '../config/queryKeys';

interface UseCustomersParams extends CustomerSearchDto {
  enabled?: boolean;
}

export function useCustomers(params: UseCustomersParams = {}) {
  const { enabled = true, ...searchDto } = params;

  // Create a stable key by extracting the relevant filters
  const filters = {
    searchTerm: searchDto.searchTerm,
    page: searchDto.page,
    pageSize: searchDto.pageSize,
    statuses: searchDto.statuses,
    assignedSalesPersonId: searchDto.assignedSalesPersonId,
  };

  const query = useQuery({
    queryKey: queryKeys.customers.list(filters),
    queryFn: () => customerService.getCustomers(searchDto),
    enabled,
    placeholderData: (prev) => prev, // Keep previous while loading
  });

  return {
    ...query,
    // Expose both for proper stale-while-revalidate handling:
    // - isLoading: true only on initial load with no cached data
    // - isFetching: true whenever a fetch is in progress (including background refetch)
    // - isStale: true when data is older than staleTime
  };
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customers.detail(id!),
    queryFn: () => customerService.getCustomer(id!),
    enabled: !!id,
  });
}

export function useCustomerCount(searchDto?: CustomerSearchDto) {
  return useQuery({
    queryKey: queryKeys.customers.count(searchDto),
    queryFn: () => customerService.getCustomerCount(searchDto),
  });
}

export function useCustomersBySalesPerson(salesPersonId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customers.bySalesPerson(salesPersonId!),
    queryFn: () => customerService.getCustomersBySalesPerson(salesPersonId!),
    enabled: !!salesPersonId,
  });
}

// Mutations
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerDto) => customerService.createCustomer(data),
    onSuccess: () => {
      // Invalidate customer lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.count() });
    },
  });
}

export function useCreateCustomerWithJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerWithJobDto) =>
      customerService.createCustomerWithJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.count() });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCustomerDto) => customerService.updateCustomer(data),
    onSuccess: (updatedCustomer) => {
      // Update the specific customer in cache
      queryClient.setQueryData(
        queryKeys.customers.detail(updatedCustomer.id),
        updatedCustomer
      );
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.deleteCustomer(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.customers.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.count() });
      // Also invalidate related data
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}
